import React, { useState } from "react";
import { visibleAccent } from "../utils/colourContrast";

const DEFAULT_ACCENT_COLOUR = "#1e1b4b";

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  color1?: string;
  color2?: string;
  actionText?: string; // "Continue with Google" or "Create Account"
}

const TermsModal: React.FC<TermsModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  color1 = DEFAULT_ACCENT_COLOUR,
  color2,
  actionText = "Continue",
}) => {
  const [termsAccepted, setTermsAccepted] = useState(false);

  // The card is white: a white colour 1 falls back to colour 2, then a dark
  // default, so the links and the accept button stay visible.
  const linkColor = visibleAccent(color1, color2, DEFAULT_ACCENT_COLOUR);

  const handleAccept = () => {
    if (termsAccepted) {
      setTermsAccepted(false); // Reset for next use
      onAccept();
    }
  };

  const handleClose = () => {
    setTermsAccepted(false); // Reset on close
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "12px",
          padding: "24px",
          maxWidth: "450px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            margin: "0 0 16px 0",
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          Terms & Conditions
        </h3>
        <p
          style={{
            margin: "0 0 20px 0",
            fontSize: "14px",
            color: "#666",
            lineHeight: "1.5",
          }}
        >
          Before continuing, please review and accept our Terms & Conditions and Privacy Policy.
        </p>

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <input
            type="checkbox"
            id="terms-modal-checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            style={{
              marginTop: "2px",
              width: "18px",
              height: "18px",
              cursor: "pointer",
              accentColor: linkColor,
            }}
          />
          <label
            htmlFor="terms-modal-checkbox"
            style={{
              fontSize: "14px",
              color: "#333",
              cursor: "pointer",
              lineHeight: "1.4",
            }}
          >
            I agree to the{" "}
            <a
              href="/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: linkColor, textDecoration: "underline" }}
            >
              Terms & Conditions
            </a>{" "}
            and{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: linkColor, textDecoration: "underline" }}
            >
              Privacy Policy
            </a>
          </label>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={handleClose}
            style={{
              padding: "10px 20px",
              borderRadius: "6px",
              border: "1px solid #ddd",
              backgroundColor: "#fff",
              color: "#666",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={!termsAccepted}
            style={{
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: termsAccepted ? linkColor : "#ccc",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: termsAccepted ? "pointer" : "not-allowed",
              opacity: termsAccepted ? 1 : 0.7,
            }}
          >
            {actionText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
